import { managerialIsoDate } from '@/lib/managerial-calendar'
import type { ManagerialDashboardSalesEvolutionPoint } from '@/types/api'

export type SalesEvolutionPeriodId = '7d' | '30d' | 'month' | 'all' | 'custom'

export type SalesEvolutionPoint = {
  date: string
  soldNumbers: number
  revenue: number
}

export type SalesEvolutionRange = {
  dateFrom: string
  dateTo: string
}

export type SalesEvolutionChartQuery = {
  period: SalesEvolutionPeriodId
  customFrom: string
  customTo: string
}

export const DEFAULT_SALES_EVOLUTION_CHART_QUERY: SalesEvolutionChartQuery = {
  period: '30d',
  customFrom: '',
  customTo: '',
}

export const SALES_EVOLUTION_PERIODS: ReadonlyArray<{
  id: SalesEvolutionPeriodId
  label: string
}> = [
  { id: '7d', label: '7 dias' },
  { id: '30d', label: '30 dias' },
  { id: 'month', label: 'Mês atual' },
  { id: 'all', label: 'Todo o período' },
  { id: 'custom', label: 'Personalizado' },
]

export const EMPTY_SALES_EVOLUTION_POINTS: SalesEvolutionPoint[] = []

// Match ManagerialDashboardSalesEvolutionWindow::MAX_CUSTOM_DAYS on the API.
export const MAX_CUSTOM_SALES_EVOLUTION_DAYS = 731

function parseCivilDate(value: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null
  const timestamp = Date.parse(`${value}T00:00:00Z`)
  return Number.isFinite(timestamp) &&
    new Date(timestamp).toISOString().slice(0, 10) === value
    ? timestamp
    : null
}

/** Invalid intermediate edits must not invalidate unrelated dashboard measurements. */
export function getSalesEvolutionChartNotice(
  chart: SalesEvolutionChartQuery,
  now = new Date()
): string | null {
  if (chart.period !== 'custom') return null
  let issue: string | null = null
  const from = parseCivilDate(chart.customFrom)
  const to = parseCivilDate(chart.customTo)
  if (!chart.customFrom || !chart.customTo) issue = 'Complete as duas datas.'
  else if (from === null || to === null) issue = 'Informe datas válidas para o gráfico.'
  else if (from > to) issue = 'A data final deve ser igual ou posterior à data inicial.'
  else if (chart.customTo > managerialIsoDate(now))
    issue = 'Não selecione datas futuras para o gráfico.'
  else if ((to - from) / 86_400_000 > MAX_CUSTOM_SALES_EVOLUTION_DAYS) {
    issue = 'O intervalo personalizado pode ter no máximo 731 dias.'
  }
  return issue === null
    ? null
    : `${issue} Enquanto isso, o gráfico mostra os últimos 30 dias e os demais indicadores continuam atualizando.`
}

function shiftDays(value: Date, amount: number): Date {
  const next = new Date(value)
  next.setUTCDate(next.getUTCDate() + amount)
  return next
}

function formatCivilDate(value: Date): string {
  return value.toISOString().slice(0, 10)
}

export function formatSalesEvolutionTick(isoDate: string): string {
  const parts = isoDate.split('-')
  const day = parts[2]
  const month = parts[1]
  if (!day || !month) return isoDate
  return `${day}/${month}`
}

export function formatSoldNumbersAxis(value: number): string {
  return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(value)
}

export function formatRevenueAxis(value: number): string {
  if (value === 0) return 'R$ 0'
  return `R$ ${formatSoldNumbersAxis(value)}`
}

export function resolveSalesEvolutionRange(
  period: SalesEvolutionPeriodId,
  custom: { from: string; to: string },
  now: Date = new Date()
): SalesEvolutionRange {
  const today = managerialIsoDate(now)
  // UTC is used only for arithmetic on a date already resolved in Brasília.
  const current = new Date(`${today}T00:00:00Z`)

  if (period === '7d') {
    return { dateFrom: formatCivilDate(shiftDays(current, -6)), dateTo: today }
  }

  if (period === '30d') {
    return { dateFrom: formatCivilDate(shiftDays(current, -29)), dateTo: today }
  }

  if (period === 'month') {
    return { dateFrom: `${today.slice(0, 7)}-01`, dateTo: today }
  }

  if (period === 'all') {
    return { dateFrom: today, dateTo: today }
  }

  const from = custom.from || formatCivilDate(shiftDays(current, -29))
  const to = custom.to || today
  if (from > to) {
    return { dateFrom: to, dateTo: from }
  }
  return { dateFrom: from, dateTo: to }
}

export function mapSalesEvolutionPoints(
  points: ManagerialDashboardSalesEvolutionPoint[]
): SalesEvolutionPoint[] {
  return points.map((point) => ({
    date: point.date,
    soldNumbers: point.sold_numbers,
    revenue: point.revenue,
  }))
}
